/**
 * Consistent animation constants for AI Agent Chat
 * Provides standardized timing and easing for all chat-related animations
 */

export const CHAT_ANIMATIONS = {
  // Core animation configurations
  message: {
    type: 'spring' as const,
    stiffness: 400,
    damping: 25,
    mass: 0.8,
    delay: 0.025
  },
  
  card: {
    duration: 0.3,
    ease: 'easeOut' as const
  },
  
  transition: {
    duration: 0.2,
    ease: 'easeInOut' as const
  },
  
  // Specific animation variants
  messageEntry: {
    initial: { opacity: 0, y: 20, scale: 0.95 },
    animate: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 400,
        damping: 25,
        mass: 0.8,
        delay: 0.025
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      transition: { 
        duration: 0.15,
        ease: "easeOut" as const
      } 
    }
  },
  
  cardEntry: {
    initial: { opacity: 0, y: 10, scale: 0.95 },
    animate: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.3, 
        ease: 'easeOut' as const
      }
    },
    exit: { 
      opacity: 0, 
      y: -10, 
      scale: 0.95,
      transition: { 
        duration: 0.2, 
        ease: 'easeOut' as const
      }
    }
  },
  
  recommendationsContainer: {
    initial: { opacity: 0, y: 10 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.3,
        ease: 'easeOut' as const
      }
    },
    exit: { 
      opacity: 0, 
      y: -10,
      transition: { 
        duration: 0.2,
        ease: 'easeOut' as const
      }
    }
  },
  
  typingIndicator: {
    container: {
      initial: { opacity: 0, y: 10 },
      animate: { 
        opacity: 1, 
        y: 0,
        transition: {
          duration: 0.3,
          ease: "easeOut" as const
        }
      },
      exit: { 
        opacity: 0,
        y: -10,
        transition: {
          duration: 0.2
        }
      }
    },
    dots: {
      animate: (i: number) => ({
        y: [0, -5, 0],
        transition: {
          delay: i * 0.15,
          repeat: Infinity,
          duration: 0.5,
          repeatType: "loop" as const,
          ease: "easeInOut" as const
        }
      })
    }
  },
  
  threadItem: {
    hover: { 
      scale: 1.01,
      transition: { duration: 0.2, ease: "easeInOut" as const }
    },
    tap: { 
      scale: 0.99,
      transition: { duration: 0.1 }
    }
  }
} as const;

/**
 * Stagger configuration for multiple cards
 */
export const STAGGER_CONFIG = {
  recommendationCards: {
    staggerChildren: 0.1,
    delayChildren: 0.05
  }
} as const;

/**
 * Helper function to create staggered animation variants
 */
export const createStaggeredVariants = (baseVariants: any, staggerDelay: number) => ({
  ...baseVariants,
  animate: {
    ...baseVariants.animate,
    transition: {
      ...baseVariants.animate.transition,
      delay: (baseVariants.animate.transition?.delay || 0) + staggerDelay
    }
  }
});